import React, { FC, Fragment, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { useSelector } from 'react-redux'
import PageLink from '../../../../components/page/menuLink/MenuLink'
import { ChannelMenuLink } from '../menuLinks'
import { getFilteredMenuLinks } from '../getFilteredMenuLinks'
import { Company } from '../../../../interfaces'
import {
  channelSelector,
  isSwedishHandballSelector
} from '../../../../redux-store/channel/channel.selectors'
import { channelFontsSelector } from '../../../../redux-store/channelFonts/channelFonts.selectors'
import { useThemeFont } from '../../Theme/useThemeFont'
import { setSubmenuFontLoaded } from '../../../../redux-store/channelTheme/channelTheme.actions'
import { useChannelTheme } from '../../Theme/useChannelTheme'

import styles from './submenu.module.scss'
import TagManager from 'react-gtm-module'
import { ConditionalRender } from 'components/ConditionalRender/ConditionalRender'

export const CHANNEL_SUB_MENU_ID = 'channel-sub-menu-id'
interface Props {
  channelSlug: string
  onSelected: (id: string) => void
  selected: string
  channelType: string
  isRootPage: boolean
  menuLinks: ChannelMenuLink[]
  isNotStartPage: boolean
}

const Submenu: FC<Props> = ({
  selected,
  channelSlug,
  onSelected,
  channelType,
  isRootPage,
  menuLinks,
  isNotStartPage
}) => {
  const channel = useSelector(channelSelector)
  const channelDetails: Company = channel?.data

  const {
    meta_data: { content, store, misc }
  } = channelDetails
  const isSwedishHandball = useSelector(isSwedishHandballSelector)

  const showStatsSubmenu = isSwedishHandball
  const menuItems = useMemo(
    () =>
      getFilteredMenuLinks(menuLinks, {
        isRootPage,
        content,
        channelType,
        store,
        misc,
        showStatsSubmenu
      }),
    [content, channelType, store, misc, isRootPage]
  )

  const [loadedFont, setLoadedFont] = useState<string>()
  const { channelTheme, channelThemeSubmenuFontIsLoaded } = useChannelTheme()
  const customFontTheme = channelTheme && channelTheme.channelSubmenuFontFamily
  const channelFontList = useSelector(channelFontsSelector)
  const font = channelFontList.find((f) => f.identifier === customFontTheme)

  useThemeFont({
    channelName: channel.data.name,
    customFontTheme,
    isFontLoaded: channelThemeSubmenuFontIsLoaded,
    customFont: font,
    setLoadedAction: setSubmenuFontLoaded,
    setLoadedFont
  })

  useEffect(() => {}, [loadedFont])

  const applyHighlightTheme =
    channelTheme?.channelPrimaryHighlightColor && isRootPage

  const triggerGTMOnSubmenuClick = (submenuLink: ChannelMenuLink) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'on_channel_submenu_click',
        menuName: submenuLink.name,
        menuLink: submenuLink.link,
        channel_slug: channelSlug,
        is_root_page: isRootPage,
        channel_type: channelType
      }
    })
  }
  const handleOnSubmenuClick = (submenuLink: ChannelMenuLink) => {
    onSelected(submenuLink.id)
    triggerGTMOnSubmenuClick(submenuLink)
  }

  const excludedSubmenusInMobileApp = ['/subscriptions', '/access']

  const RenderedMenuItem = (menuLink: ChannelMenuLink) => (
    <li
      className={classNames({
        [styles.NotSelected]: menuLink.id !== selected,
        [styles.HoverTheme]: applyHighlightTheme,
        [styles.SelectedTheme]: menuLink.id === selected && applyHighlightTheme
      })}
      onClick={() => handleOnSubmenuClick(menuLink)}
      onKeyUp={(event) => {
        if (event.key === ' ') {
          handleOnSubmenuClick(menuLink)
        }
      }}
    >
      <PageLink
        {...{
          ...menuLink,
          link: `/${channelSlug}${menuLink.link}`
        }}
      />
    </li>
  )
  return (
    <div
      id={CHANNEL_SUB_MENU_ID}
      data-testid="channel-submenu"
      className={classNames(styles.SubmenuContainer, {
        [styles.Themed]: isRootPage,
        [styles.NotThemed]: !isRootPage,
        [styles.ShoudlHaveSmallerText]: isNotStartPage && !isRootPage
      })}
    >
      <ul data-testid="channel-menu">
        {menuItems.map((menuLink: ChannelMenuLink) => (
          <Fragment key={menuLink.id}>
            {/* Web */}
            <ConditionalRender mobileApp={false}>
              {RenderedMenuItem(menuLink)}
            </ConditionalRender>
            {/* MobileApp */}
            <ConditionalRender web={false}>
              {!excludedSubmenusInMobileApp.includes(menuLink.link as string) &&
                RenderedMenuItem(menuLink)}
            </ConditionalRender>
          </Fragment>
        ))}
      </ul>
    </div>
  )
}
export default Submenu
